import axios, { isAxiosError, isCancel, type AxiosResponse } from 'axios'
import { isDocumentJobEnded, type DocumentImmediateResult } from '@numeo/types-core'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import { retryTransientRequest } from '@/utils/retry-transient-request'
import type { BoxTruckProfileDocumentEntity } from './boxTruckDirectDocumentUpload'

const NON_V1_BASE = getApiBaseUrlWithoutV1()
/** How long HR waits for a read before the card says to fill the fields by hand. */
const READ_DEADLINE_MS = 30_000
/** The longest the server may hold a job poll open (seconds). */
const POLL_WAIT_SECONDS = 10
const DEFAULT_RETRY_AFTER_SECONDS = 2
/** A lost response is sent again after this long (same request key, so the server never reads the file twice). */
const LOST_RESPONSE_RETRY_MS = 1000
/**
 * Resends of one call, at most. Each waits twice as long as the one before, so a server that is down gets a few uploads per file,
 * not one every second until the deadline.
 */
const MAX_RESENDS = 3
/**
 * Time kept back from a poll's `wait` for the answer to travel back: a poll that asked the server to hold it for all the time left
 * would be cut off by its own timeout just before the server answered, and a read about to finish would report a timeout.
 */
const POLL_ANSWER_MARGIN_MS = 1500

/**
 * Why a read gave no answer: the user stopped it, it ran out of time, the server refused the file, a saved document is no longer
 * there (`gone`), or the call failed.
 */
type DocumentReadFailure = 'cancelled' | 'timeout' | 'rejected' | 'gone' | 'failed'

export class DocumentReadError extends Error {
    constructor(
        readonly kind: DocumentReadFailure,
        message: string,
        /**
         * The error this one was made from, when the read failed in a way none of the known outcomes explains (a server error, a bug):
         * the caller reports it, so a failure no rule covers is never hidden behind "could not be read".
         */
        readonly unexpected?: unknown
    ) {
        super(message)
        this.name = 'DocumentReadError'
    }
}

const retryAfterMs = (headers: AxiosResponse['headers'] | undefined): number => {
    // axios lowercases response header names; main-service exposes Retry-After to the browser (CORS Expose-Headers).
    const value = Number(headers?.['retry-after'])
    return (Number.isFinite(value) && value > 0 ? value : DEFAULT_RETRY_AFTER_SECONDS) * 1000
}

/** Answers that mean "not now": the server is busy, or the load balancer in front of it gave up waiting. */
const BUSY_STATUSES = new Set([429, 502, 503, 504])

/** axios's own timeout (a call given `timeout`): the read's time is up, not a lost response to resend. */
const AXIOS_TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT'])

/** A failure as one of the read's kinds; `retry` when the same request may simply be sent again (busy server, lost response). */
/** Which request of the read failed: uploading a file, naming a saved document, or asking after the job. */
type ReadCall = 'upload' | 'saved' | 'poll'

const classify = (error: unknown, call: ReadCall): { failure: DocumentReadError; retryInMs?: number } => {
    if (error instanceof DocumentReadError) return { failure: error }
    if (isCancel(error)) return { failure: new DocumentReadError('cancelled', 'Read cancelled') }
    if (isAxiosError(error)) {
        if (error.code && AXIOS_TIMEOUT_CODES.has(error.code)) return { failure: new DocumentReadError('timeout', 'The read took too long') }
        const status = error.response?.status
        // Busy, or a proxy in front of the server that gave up on it (502/504): the same request, under the same key, is answered
        // from the job it already started.
        if (status !== undefined && BUSY_STATUSES.has(status))
            return { failure: new DocumentReadError('failed', 'The server is busy'), retryInMs: retryAfterMs(error.response?.headers) }
        // The file refused (too large, not a document): asking again cannot help.
        if (status === 413 || status === 415) return { failure: new DocumentReadError('rejected', 'The server refused this file') }
        // A saved document our route says is not there (removed since the form loaded, its profile out of reach): no resend can
        // find it. Only an answer from our handler (`success: false`) counts: a 404 for a route not deployed yet is ours broken.
        if (status === 404 && call === 'saved' && (error.response?.data as { success?: unknown } | undefined)?.success === false)
            return { failure: new DocumentReadError('gone', 'The saved document is no longer there') }
        // A polled job the server no longer knows (ADR-022: Polling → Rejected, so it is not polled again). Nothing about the file
        // says so: it is a failed read, reported, with Try again offered (a fresh submit). A 400, or a 404 on the submit, is our
        // own request broken (a field missing, the route not deployed). All of these fall through below, so they are reported
        // rather than shown as a file that cannot be read.
        if (!error.response) return { failure: new DocumentReadError('failed', 'The response was lost'), retryInMs: LOST_RESPONSE_RETRY_MS }
    }
    // No rule above covers it (a 500, a bug in this module): still a failed read for the card, with the original kept for reporting.
    return { failure: new DocumentReadError('failed', 'The document could not be read', error ?? new Error('unknown read failure')) }
}

/** A document already saved on a Box Truck profile: the server reads it from storage, so the browser never downloads it to send back. */
export interface SavedProfileDocument {
    entity: BoxTruckProfileDocumentEntity
    entityId: string
    documentId: string
}

/** What a read sends first: the file itself, or the saved document the server reads. Either way only the job is polled after. */
type ReadSubmission = { file: File } | { saved: SavedProfileDocument }

/**
 * Read one document with the pipeline's immediate mode (ADR-022 § What the web app sees): one request key per document, reused on
 * every resend so a resend never starts a second read; a busy server (429/503) is waited out for `Retry-After`; after a 202 the job
 * is only polled, the document never sent again. Gives up after 30 s or when `signal` aborts. Resolves with the ended job, whatever
 * its status; the caller decides what the status means for its form.
 */
async function readDocument(
    submission: ReadSubmission,
    docTypeHint: string,
    requestKey: string,
    signal: AbortSignal | undefined
): Promise<DocumentImmediateResult> {
    const deadline = Date.now() + READ_DEADLINE_MS
    const timeLeft = () => deadline - Date.now()

    /**
     * One call, repeated (same arguments, the shared retry) while it fails in a way a resend can fix — a busy server after its
     * `Retry-After`, a lost answer — at most `MAX_RESENDS` times, each wait twice the one before, and only while the wait still fits
     * before the deadline. Any failure leaves as the read's own kind.
     */
    const withRetries = async (kind: ReadCall, call: () => Promise<DocumentImmediateResult>): Promise<DocumentImmediateResult> => {
        try {
            return await retryTransientRequest(call, {
                isTransient: (error) => classify(error, kind).retryInMs !== undefined,
                delayFor: (error, attempt) => {
                    const { retryInMs } = classify(error, kind)
                    if (retryInMs === undefined || attempt >= MAX_RESENDS) return undefined
                    const delay = retryInMs * 2 ** attempt
                    return delay < timeLeft() ? delay : undefined
                },
                signal,
            })
        } catch (error) {
            // Stopped by the caller, during a call or a wait: cancelled, whatever the last failure was.
            if (signal?.aborted) throw new DocumentReadError('cancelled', 'Read cancelled')
            throw classify(error, kind).failure
        }
    }

    const submit = async (): Promise<DocumentImmediateResult> => {
        // `timeout`: the deadline covers a call in flight too (a slow upload), not only the gaps between calls.
        const options = { baseURL: NON_V1_BASE, signal, timeout: Math.max(1, timeLeft()) }
        if ('saved' in submission) {
            const { entity, entityId, documentId } = submission.saved
            const path = `/box-truck-loads/profiles/${entity}/${encodeURIComponent(entityId)}/documents/${encodeURIComponent(documentId)}/read/v1`
            return (await axios.post<{ data: DocumentImmediateResult }>(path, { requestKey, docTypeHint }, options)).data.data
        }
        // Field order matters: the server reads the key and the type before the file (multipart is streamed).
        const form = new FormData()
        form.append('requestKey', requestKey)
        form.append('docTypeHint', docTypeHint)
        form.append('file', submission.file)
        return (await axios.post<{ data: DocumentImmediateResult }>('/documents/processing/immediate/v1', form, options)).data.data
    }

    const poll = async (jobId: string): Promise<DocumentImmediateResult> => {
        // Under a second to wait: a poll could only be answered "still running" at once, again and again, so the read ends here.
        const wait = Math.min(POLL_WAIT_SECONDS, Math.floor((timeLeft() - POLL_ANSWER_MARGIN_MS) / 1000))
        if (wait < 1) throw new DocumentReadError('timeout', 'The read took too long')
        const response = await axios.get<{ data: DocumentImmediateResult }>(`/documents/processing/jobs/${encodeURIComponent(jobId)}/v1`, {
            baseURL: NON_V1_BASE,
            params: { wait },
            signal,
            timeout: Math.max(1, timeLeft()),
        })
        return response.data.data
    }

    let job = await withRetries('saved' in submission ? 'saved' : 'upload', submit)
    while (!isDocumentJobEnded(job.status)) {
        const { jobId } = job
        job = await withRetries('poll', () => poll(jobId))
    }
    return job
}

/** Read a file chosen in the form (not saved yet): it is uploaded with the request. */
export const readProfileDocument = (file: File, docTypeHint: string, requestKey: string, signal?: AbortSignal): Promise<DocumentImmediateResult> =>
    readDocument({ file }, docTypeHint, requestKey, signal)

/** Read a document already saved on the profile: the server reads it from storage. */
export const readSavedProfileDocument = (
    saved: SavedProfileDocument,
    docTypeHint: string,
    requestKey: string,
    signal?: AbortSignal
): Promise<DocumentImmediateResult> => readDocument({ saved }, docTypeHint, requestKey, signal)
